import { Modal } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import clsx from "clsx";
import RSButton from "ds_legacy/components/RSButton";
import { HINT_NUDGE_BACKGROUND } from "ds_legacy/materials/colors";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import {
  Body,
  FONT_SIZE_16,
  LINE_HEIGHT_20,
} from "ds_legacy/materials/typography";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

const buttonStyles = {
  margin: margin(0),
};

export const NUDGE_LOCALSTORAGE_KEY = "didShowFiltersNudge";
const NUDGE_LOCALSTORAGE_VALUE = "true";

const useShowNudge = (): [boolean, Dispatch<SetStateAction<boolean>>] => {
  const [showNudge, setShowNudge] = useState<boolean>(false);

  useEffect(() => {
    if ((window as { IN_STORYBOOK: boolean }).IN_STORYBOOK) return;

    const didShowFiltersNudge = window.localStorage.getItem(
      NUDGE_LOCALSTORAGE_KEY,
    );
    if (didShowFiltersNudge === NUDGE_LOCALSTORAGE_VALUE) return;

    window.localStorage.setItem(
      NUDGE_LOCALSTORAGE_KEY,
      NUDGE_LOCALSTORAGE_VALUE,
    );

    const timeout = setTimeout(() => {
      setShowNudge(true);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [showNudge, setShowNudge]);

  return [showNudge, setShowNudge];
};

export function HintNudge({
  confirmText,
  heading,
  isVisible,
  position,
  text,
}: {
  confirmText: string;
  heading?: string;
  isVisible?: boolean;
  position: { x: number; y: number };
  text: string;
}) {
  const [showNudge, setShowNudge] = useShowNudge();

  const shouldShow = isVisible ?? showNudge;
  const firstFocusableElement = useFocusElement<HTMLButtonElement>({
    dependencies: [shouldShow],
    shouldFocus: !shouldShow,
  });

  if (!shouldShow) return null;

  return (
    <Modal open={shouldShow}>
      <div
        className={clsx(
          "absolute min-h-[104px] max-w-[344px] rounded-[2px] p-[8px]",
          "max-[600px]:top-[184px] max-[600px]:right-[8px] max-[600px]:left-[8px]",
        )}
        style={{
          background: HINT_NUDGE_BACKGROUND,
          left: dp(position.x),
          top: dp(position.y),
        }}
      >
        <h2 style={visuallyHidden}>{heading}</h2>
        <div
          className="absolute -top-[9px] left-[8px] h-0 w-0"
          style={{
            borderBottom: `14px solid ${HINT_NUDGE_BACKGROUND}`,
            borderLeft: "12px solid transparent",
            borderRight: "12px solid transparent",
          }}
        />
        <VerticalLayout
          margin={margin(3, 0.5, 0, 0)}
          padding={padding(0, 1, 1, 0)}
        >
          <Body fontSize={FONT_SIZE_16} lineHeight={LINE_HEIGHT_20}>
            {`☝🏻 ${text}`}
          </Body>
          <div className="flex w-full justify-end">
            <RSButton
              ref={firstFocusableElement}
              color="primary"
              id="got_it"
              loading="na"
              style={buttonStyles}
              variant="text"
              onClick={() => {
                if (isVisible !== undefined) {
                  // If isVisible prop is provided, we can't control the state
                  // This is for Storybook/controlled usage
                  return;
                }
                setShowNudge(false);
              }}
            >
              {confirmText}
            </RSButton>
          </div>
        </VerticalLayout>
      </div>
    </Modal>
  );
}
